import { World, TPS } from '../sim/world';
import { initRealm, realmSpawnPoint, spawnRevenantNear } from '../sim/realm';
import { createPlayer, removePlayer, applyInput, ghostFromPlayer } from '../sim/player';
import { applyBuild, respecIsFree } from '../sim/build';
import { applyCrowdEffect } from '../sim/crowd';
import { buildSnapshot, selfView } from '../sim/snapshot';
import type { EntityId, GhostSnapshot, HeroSave, PlayerEnt, MobEnt } from '../sim/types';
import type { SimEvent } from '../sim/events';
import type { C2S, S2C, DeathCard, SpectateTarget } from '../protocol/messages';
import type { HeroId } from '../content/heroes';
import { maskProfanity } from '../content/profanity';
import { CROWD_EFFECTS, CROWD_ESCALATION, CROWD_HARM_PER_MINUTE, CROWD_MARTYR_FAME, CROWD_SHARE } from '../content/crowd';
import { RESPEC_FAME } from '../content/stats';
import { AOI_RADIUS, type RoomHost, type Session } from './types';

export interface PetChoice {
  id: string;
  level: number;
}

interface Client {
  session: Session;
  playerId: EntityId;
  known: Set<EntityId>;
  heroId: HeroId;
  lastChat: number;
  spawnIndex: number;
  deathSent: boolean;
  seed: number;
  pet: PetChoice | null;
  /** show mode: purchases of each effect against this hero in this realm (price escalation) */
  crowdBought: Map<string, number>;
  /** ticks of recent harm purchases (per-minute budget) */
  crowdHarmTicks: number[];
  /** leaving is a one-second cast: the tick it completes, and the tick it started (to spot hits) */
  leaveAt: number;
  leaveFrom: number;
}

/** A session without a hero, following one player's camera. */
interface Spectator {
  session: Session;
  targetSid: number;
  known: Set<EntityId>;
  lastChat: number;
  /** last purchase tick per effect */
  lastBuy: Map<string, number>;
  lastAnyBuy: number;
}

const GLOBAL_EVENTS = new Set(['announce', 'feed', 'shake', 'crowd']);
/** seconds between purchases by the same viewer */
const CROWD_VIEWER_COOLDOWN = 15;
/** leaving a realm alive is a cast, so it cannot be used to dodge a killing blow */
export const LEAVE_CAST_SEC = 1;
/** seconds a crowd effect keeps counting toward a martyr's death */
const MARTYR_WINDOW = 45;

export class RealmRoom {
  readonly world: World;
  readonly clients = new Map<number, Client>();
  readonly spectators = new Map<number, Spectator>();
  readonly createdAt: number;
  closed = false;
  private spawnCounter = 0;

  constructor(
    readonly code: string,
    readonly seed: number,
    readonly bossDef: string,
    ghosts: GhostSnapshot[],
    readonly host: RoomHost,
  ) {
    this.world = new World('realm', seed);
    initRealm(this.world, code, bossDef, ghosts);
    this.createdAt = Date.now();
    this.world.listeners.onPlayerDeath = (p) => this.onDeath(p);
    this.world.listeners.onRevenantKilled = (rev, killer) => {
      if (rev.ghost && this.host.onRevenantSlain) {
        const name = killer ? (killer.kind === 'player' ? (killer as PlayerEnt).name : '') : '';
        this.host.onRevenantSlain(this, rev.ghost, name);
      }
    };
  }

  get playerCount(): number {
    return this.clients.size;
  }

  get phase() {
    return this.world.realm!.phase;
  }

  /** spawn index of the most recent joiner, so invited friends appear next to each other */
  lastSpawnIndex = 0;

  join(session: Session, heroId: HeroId, avenge?: GhostSnapshot, spawnIndex?: number, pet: PetChoice | null = null, save: HeroSave | null = null): void {
    if (this.clients.has(session.sid)) this.leave(session.sid);
    if (this.spectators.has(session.sid)) this.unwatch(session.sid, false);
    const idx = spawnIndex ?? this.spawnCounter++;
    this.lastSpawnIndex = idx;
    const sp = realmSpawnPoint(this.world, idx);
    const p = createPlayer(this.world, { accountId: session.accountId, name: session.name, heroId: save?.heroId ?? heroId, team: 1, x: sp.x, y: sp.y, seed: (this.seed ^ (session.sid * 7919) ^ idx) >>> 0, pet, save });
    const client: Client = { session, playerId: p.id, known: new Set(), heroId: p.heroId, lastChat: 0, spawnIndex: idx, deathSent: false, seed: p.seed, pet, crowdBought: new Map(), crowdHarmTicks: [], leaveAt: 0, leaveFrom: 0 };
    this.clients.set(session.sid, client);
    session.send({ t: 'welcome', id: p.id, accountId: session.accountId, tick: this.world.tick, mode: 'realm', map: this.world.map.descriptor(), code: this.code, team: p.team, tps: TPS });
    this.sendRealmState(client);
    if (avenge) {
      if (avenge.accountId !== session.accountId) spawnRevenantNear(this.world, avenge, p);
    }
    this.world.events.push({ t: 'feed', key: 'feed.joined', vars: { name: session.name } });
  }

  leave(sid: number): void {
    const c = this.clients.get(sid);
    if (!c) return;
    const p = this.world.players.get(c.playerId);
    if (p && !p.dead) this.host.onPlayerResult(this, p, 'leave');
    removePlayer(this.world, c.playerId);
    this.clients.delete(sid);
    this.world.events.push({ t: 'feed', key: 'feed.left', vars: { name: c.session.name } });
    // the show is over for whoever followed this hero
    for (const [ssid, s] of this.spectators) if (s.targetSid === sid) this.endSpectate(ssid, 'left');
  }

  /** Updates the companion used on the next respawn (e.g. after a level-up between runs). */
  setPet(sid: number, pet: PetChoice | null): void {
    const c = this.clients.get(sid);
    if (c) c.pet = pet;
  }

  respawn(sid: number, heroId: HeroId): void {
    const c = this.clients.get(sid);
    if (!c) return;
    const old = this.world.players.get(c.playerId);
    if (old && !old.dead) return;
    if (old) removePlayer(this.world, c.playerId);
    const sp = realmSpawnPoint(this.world, c.spawnIndex);
    const p = createPlayer(this.world, { accountId: c.session.accountId, name: c.session.name, heroId, team: 1, x: sp.x, y: sp.y, seed: (c.seed + 1013904223) >>> 0, pet: c.pet });
    c.playerId = p.id;
    c.heroId = heroId;
    c.deathSent = false;
    c.seed = p.seed;
    c.known.clear();
    c.crowdBought.clear();
    c.crowdHarmTicks = [];
    c.leaveAt = 0;
    c.session.send({ t: 'welcome', id: p.id, accountId: c.session.accountId, tick: this.world.tick, mode: 'realm', map: this.world.map.descriptor(), code: this.code, team: p.team, tps: TPS });
    this.sendRealmState(c);
    for (const [ssid, s] of this.spectators) {
      if (s.targetSid !== sid) continue;
      s.known.clear();
      this.sendSpectateWelcome(s, c);
    }
  }

  /**
   * Starts the leave cast for a living hero. Returns false when there is nothing to cast
   * (no such client, or a dead hero) and the caller should pull the session out at once.
   */
  beginLeave(sid: number): boolean {
    const c = this.clients.get(sid);
    if (!c) return false;
    const p = this.world.players.get(c.playerId);
    if (!p || p.dead) return false;
    if (!c.leaveAt) {
      c.leaveFrom = this.world.tick;
      c.leaveAt = this.world.tick + Math.round(LEAVE_CAST_SEC * TPS);
      c.session.send({ t: 'leaving', sec: LEAVE_CAST_SEC });
    }
    return true;
  }

  cancelLeave(sid: number, reason?: string): void {
    const c = this.clients.get(sid);
    if (!c || !c.leaveAt) return;
    c.leaveAt = 0;
    c.session.send({ t: 'leaving', sec: 0, cancelled: true, reason });
  }

  /** Runs every tick: a hit breaks the cast, otherwise the host is told the hero may go. */
  private stepLeaving(): void {
    for (const [sid, c] of [...this.clients]) {
      if (!c.leaveAt) continue;
      const p = this.world.players.get(c.playerId);
      if (!p || p.dead) {
        c.leaveAt = 0;
        continue;
      }
      if (p.lastHurt >= c.leaveFrom) {
        this.cancelLeave(sid, 'toast.leaveHit');
        continue;
      }
      if (this.world.tick >= c.leaveAt) {
        c.leaveAt = 0;
        this.host.onLeaveReady?.(this, c.session);
      }
    }
  }

  // ------------------------------------------------------------ spectators (show mode)

  /** The session that plays the given account, if it is in this realm. */
  clientOfAccount(accountId: string): Client | undefined {
    for (const c of this.clients.values()) if (c.session.accountId === accountId) return c;
    return undefined;
  }

  watchersOf(sid: number): number {
    let n = 0;
    for (const s of this.spectators.values()) if (s.targetSid === sid) n++;
    return n;
  }

  private targetInfo(c: Client): SpectateTarget {
    return { id: c.playerId, name: c.session.name, heroId: c.heroId, accountId: c.session.accountId, showMode: this.host.showModeOf ? this.host.showModeOf(c.session.accountId) : false };
  }

  private sendSpectateWelcome(s: Spectator, target: Client): void {
    const info = this.targetInfo(target);
    s.session.send({ t: 'welcome', id: target.playerId, accountId: s.session.accountId, tick: this.world.tick, mode: 'realm', map: this.world.map.descriptor(), code: this.code, team: 0, tps: TPS, spectate: info });
    s.session.send({ t: 'spectate', target: info });
    const st = this.world.realm!;
    s.session.send({ t: 'realm', phase: st.phase, corruption: Math.min(1, st.corruption / st.corruptionNeeded), players: this.clients.size, boss: st.bossDef, closeIn: st.phase === 'closing' ? Math.max(0, Math.round((st.closeAt - this.world.tick) / TPS)) : 0 });
  }

  /** Attaches a hero-less session to a player's camera; returns false when the target is not here. */
  watch(session: Session, targetAccountId: string): boolean {
    const target = this.clientOfAccount(targetAccountId);
    if (!target || target.session.accountId === session.accountId) return false;
    if (this.clients.has(session.sid)) this.leave(session.sid);
    const prev = this.spectators.get(session.sid);
    const s: Spectator = { session, targetSid: [...this.clients.entries()].find(([, c]) => c === target)![0], known: new Set(), lastChat: 0, lastBuy: prev?.lastBuy ?? new Map(), lastAnyBuy: prev?.lastAnyBuy ?? -99999 };
    this.spectators.set(session.sid, s);
    this.sendSpectateWelcome(s, target);
    this.world.events.push({ t: 'feed', key: 'feed.watching', vars: { name: session.name, target: target.session.name } });
    return true;
  }

  unwatch(sid: number, notify = true): void {
    const s = this.spectators.get(sid);
    if (!s) return;
    this.spectators.delete(sid);
    if (notify) s.session.send({ t: 'spectate', target: null });
  }

  private endSpectate(sid: number, reason: string): void {
    const s = this.spectators.get(sid);
    if (!s) return;
    this.spectators.delete(sid);
    s.session.send({ t: 'spectate', target: null, reason });
  }

  /** Current fame price of an effect against a hero: escalates per purchase, doubles during the boss. */
  crowdPrice(target: Client, effectId: string): number {
    const def = CROWD_EFFECTS[effectId];
    if (!def) return 0;
    const bought = target.crowdBought.get(effectId) ?? 0;
    const boss = this.world.realm?.phase === 'boss' ? 2 : 1;
    return Math.round(def.cost * (1 + CROWD_ESCALATION * bought) * boss);
  }

  crowdPrices(target: Client): Record<string, number> {
    const out: Record<string, number> = {};
    for (const id of Object.keys(CROWD_EFFECTS)) out[id] = this.crowdPrice(target, id);
    return out;
  }

  private crowd(sid: number, effectId: string): void {
    const s = this.spectators.get(sid);
    if (!s) return;
    const reply = (ok: boolean, err?: string, target?: Client) => s.session.send({ t: 'crowd', ok, err, prices: target ? this.crowdPrices(target) : undefined });
    const def = CROWD_EFFECTS[effectId];
    if (!def) return reply(false, 'crowd.unknown');
    const target = this.clients.get(s.targetSid);
    const p = target ? this.world.players.get(target.playerId) : undefined;
    if (!target || !p || p.dead) return reply(false, 'crowd.noTarget');
    if (!(this.host.showModeOf?.(target.session.accountId) ?? false)) return reply(false, 'crowd.notShow', target);
    const now = this.world.tick;
    if (now - s.lastAnyBuy < CROWD_VIEWER_COOLDOWN * TPS) return reply(false, 'crowd.cooldown', target);
    if (now - (s.lastBuy.get(effectId) ?? -99999) < def.cooldown * TPS) return reply(false, 'crowd.cooldown', target);
    if (def.kind === 'harm') {
      target.crowdHarmTicks = target.crowdHarmTicks.filter((t) => now - t < 60 * TPS);
      if (target.crowdHarmTicks.length >= CROWD_HARM_PER_MINUTE) return reply(false, 'crowd.budget', target);
      if (now - p.spawnTick < 20 * TPS) return reply(false, 'crowd.grace', target);
    }
    const price = this.crowdPrice(target, effectId);
    if (!this.host.crowdPay) return reply(false, 'crowd.offline', target);
    if (!this.host.crowdPay(s.session, target.session.accountId, price, CROWD_SHARE)) return reply(false, 'crowd.noFame', target);
    const refused = applyCrowdEffect(this.world, p, def, s.session.name);
    if (refused) {
      // refund: the realm state made the effect impossible
      this.host.crowdPay(s.session, target.session.accountId, -price, 0);
      return reply(false, refused.err, target);
    }
    s.lastAnyBuy = now;
    s.lastBuy.set(effectId, now);
    target.crowdBought.set(effectId, (target.crowdBought.get(effectId) ?? 0) + 1);
    if (def.kind === 'harm') target.crowdHarmTicks.push(now);
    reply(true, undefined, target);
  }

  // ------------------------------------------------------------ messages

  handle(sid: number, msg: C2S): void {
    const c = this.clients.get(sid);
    if (!c) {
      this.handleSpectator(sid, msg);
      return;
    }
    switch (msg.t) {
      case 'input': {
        const p = this.world.players.get(c.playerId);
        if (p && !p.dead) applyInput(p, msg.i);
        break;
      }
      case 'respawn':
        this.respawn(sid, msg.heroId);
        break;
      case 'chat': {
        const now = Date.now();
        if (now - c.lastChat < 1000) return;
        c.lastChat = now;
        const text = maskProfanity(msg.text.replace(/\s+/g, ' ').trim().slice(0, 200));
        if (!text) return;
        if (this.host.onChat && !this.host.onChat(this, c.session, text)) return;
        this.broadcast({ t: 'chat', from: c.session.name, text, id: c.playerId });
        break;
      }
      case 'build': {
        const p = this.world.players.get(c.playerId);
        if (!p || p.dead) return;
        if (msg.op.op === 'respec' && !respecIsFree(p.rank)) {
          if (this.host.spendFame && !this.host.spendFame(c.session, RESPEC_FAME)) {
            c.session.send({ t: 'build', ok: false, err: 'build.noFame' });
            return;
          }
        }
        const r = applyBuild(this.world, p, msg.op);
        c.session.send({ t: 'build', ok: r.ok, err: r.err });
        break;
      }
      case 'leaveCancel':
        this.cancelLeave(sid);
        break;
      case 'ping':
        c.session.send({ t: 'pong', n: msg.n, tick: this.world.tick });
        break;
      default:
        break;
    }
  }

  private handleSpectator(sid: number, msg: C2S): void {
    const s = this.spectators.get(sid);
    if (!s) return;
    switch (msg.t) {
      case 'chat': {
        const now = Date.now();
        if (now - s.lastChat < 2000) return;
        s.lastChat = now;
        const text = maskProfanity(msg.text.replace(/\s+/g, ' ').trim().slice(0, 200));
        if (!text) return;
        if (this.host.onChat && !this.host.onChat(this, s.session, text)) return;
        const m: S2C = { t: 'chat', from: s.session.name, text, spectator: true };
        this.broadcast(m);
        break;
      }
      case 'crowd':
        this.crowd(sid, msg.id);
        break;
      case 'unwatch':
        this.unwatch(sid);
        break;
      case 'ping':
        s.session.send({ t: 'pong', n: msg.n, tick: this.world.tick });
        break;
      default:
        break;
    }
  }

  private onDeath(p: PlayerEnt): void {
    let client: Client | undefined;
    for (const c of this.clients.values()) if (c.playerId === p.id) client = c;
    if (!client || client.deathSent) return;
    client.deathSent = true;
    const ghostId = this.host.newGhostId();
    const ghost = ghostFromPlayer(p, ghostId, this.world.tick - p.spawnTick);
    const martyr = this.world.tick - p.crowdFedAt < MARTYR_WINDOW * TPS;
    const card: DeathCard = {
      ghostId,
      name: p.name,
      heroId: p.heroId,
      level: p.level,
      rank: p.rank,
      kills: p.kills,
      bossKills: p.bossKills,
      revenantKills: p.revenantKills,
      damageDealt: Math.round(p.damageDealt),
      survivedSec: Math.round((this.world.tick - p.spawnTick) / TPS),
      killer: p.deathCause ?? { name: '', kind: 'unknown' },
      build: { actives: ghost.actives, autos: ghost.autos, traits: ghost.traits, keystones: ghost.keystones, weapon: ghost.weapon },
      fame: p.fameEarned + Math.round(p.level * 2 + (p.kills - p.carried.kills) * 0.2) + (martyr ? CROWD_MARTYR_FAME : 0),
      realmCode: this.code,
      martyr: martyr || undefined,
    };
    p.fameEarned = card.fame;
    client.session.send({ t: 'death', card });
    // the fallen hero joins the pool for other players in this realm too
    if (p.level >= 3) this.world.realm!.ghosts.push(ghost);
    this.host.onDeath(this, p, card, ghost);
  }

  broadcast(m: S2C): void {
    for (const c of this.clients.values()) c.session.send(m);
    for (const s of this.spectators.values()) s.session.send(m);
  }

  private sendRealmState(c: Client | Spectator): void {
    const st = this.world.realm!;
    c.session.send({
      t: 'realm',
      phase: st.phase,
      corruption: Math.min(1, st.corruption / st.corruptionNeeded),
      players: this.clients.size,
      boss: st.bossDef,
      closeIn: st.phase === 'closing' ? Math.max(0, Math.round((st.closeAt - this.world.tick) / TPS)) : 0,
    });
  }

  private eventsAround(globals: SimEvent[], local: SimEvent[], cx: number, cy: number, playerId: EntityId): SimEvent[] {
    const r2 = (AOI_RADIUS + 200) * (AOI_RADIUS + 200);
    const mine: SimEvent[] = globals.length ? [...globals] : [];
    for (const e of local) {
      if (e.t === 'pickup') {
        if (e.id === playerId) mine.push(e);
        continue;
      }
      if ('x' in e) {
        const dx = e.x - cx;
        const dy = e.y - cy;
        if (dx * dx + dy * dy <= r2) mine.push(e);
      } else mine.push(e);
    }
    return mine;
  }

  step(): void {
    if (this.closed) return;
    this.world.step();
    this.stepLeaving();
    const events = this.world.events.drain();
    const globals: SimEvent[] = [];
    const local: SimEvent[] = [];
    for (const e of events) {
      if (e.t === 'status') continue;
      if (GLOBAL_EVENTS.has(e.t)) globals.push(e);
      else local.push(e);
    }
    for (const c of this.clients.values()) {
      const p = this.world.players.get(c.playerId);
      const cx = p ? p.x : this.world.map.center.x;
      const cy = p ? p.y : this.world.map.center.y;
      const mine = this.eventsAround(globals, local, cx, cy, c.playerId);
      if (mine.length) c.session.send({ t: 'ev', e: mine });
      // periodically resend static fields so a dropped first snapshot never leaves units nameless
      if ((this.world.tick + c.playerId) % 100 === 0) c.known.clear();
      const snap = buildSnapshot(this.world, p ?? null, c.known, cx, cy, AOI_RADIUS);
      c.session.send({ t: 'snap', s: snap });
      if (this.world.tick % 20 === 0) this.sendRealmState(c);
    }
    for (const [sid, s] of this.spectators) {
      const target = this.clients.get(s.targetSid);
      const p = target ? this.world.players.get(target.playerId) : undefined;
      if (!target || !p) {
        this.endSpectate(sid, 'left');
        continue;
      }
      const mine = this.eventsAround(globals, local, p.x, p.y, p.id);
      if (mine.length) s.session.send({ t: 'ev', e: mine });
      if ((this.world.tick + sid) % 100 === 0) s.known.clear();
      const snap = buildSnapshot(this.world, null, s.known, p.x, p.y, AOI_RADIUS);
      snap.me = selfView(this.world, p);
      s.session.send({ t: 'snap', s: snap });
      if (this.world.tick % 20 === 0) this.sendRealmState(s);
    }
    const st = this.world.realm!;
    if (st.phase === 'closed' && !this.closed) {
      this.closed = true;
      for (const c of this.clients.values()) {
        const p = this.world.players.get(c.playerId);
        if (p && !p.dead) this.host.onPlayerResult(this, p, 'realmEnd');
      }
      for (const sid of [...this.spectators.keys()]) this.endSpectate(sid, 'closed');
      this.host.onRealmClosed(this);
    }
  }

  /** Used by the host to move everyone to a new realm; `player` is the living hero to carry over. */
  sessionsWithHeroes(): { session: Session; heroId: HeroId; pet: PetChoice | null; player: PlayerEnt | undefined }[] {
    return [...this.clients.values()].map((c) => {
      const p = this.world.players.get(c.playerId);
      return { session: c.session, heroId: c.heroId, pet: c.pet, player: p && !p.dead ? p : undefined };
    });
  }

  /** Seconds the client's current hero has been in this realm. */
  sessionSecOf(sid: number): number {
    const c = this.clients.get(sid);
    const p = c ? this.world.players.get(c.playerId) : undefined;
    return p ? (this.world.tick - p.spawnTick) / TPS : 0;
  }

  spawnIndexOf(sid: number): number | undefined {
    return this.clients.get(sid)?.spawnIndex;
  }

  bossEntity(): MobEnt | undefined {
    const id = this.world.realm?.bossId;
    return id ? this.world.mobs.get(id) : undefined;
  }
}
